import { Fragment } from "react";
import { Check, Minus } from "lucide-react";
import type {
  NotionBlock,
  NotionProperty,
  NotionRichText,
} from "@colab/shared";
import { cn } from "@/lib/utils";

// Render-only view of the normalized Notion block tree (see @colab/shared
// notion-kb DTOs). Deliberately styled with the admin design tokens (font-
// display headings, accent links, muted dividers) rather than a bespoke prose
// theme — this is an internal reference surface, so it should read like the
// rest of /admin.

function RichText({ runs }: { runs: NotionRichText[] }) {
  if (runs.length === 0) return null;
  return (
    <>
      {runs.map((run, i) => {
        if (run.text === "") return null;
        let node: React.ReactNode = run.text;
        if (run.code) {
          node = (
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
              {node}
            </code>
          );
        }
        const className = cn(
          run.bold && "font-semibold",
          run.italic && "italic",
          run.strikethrough && "line-through",
          run.underline && "underline",
        );
        if (className) node = <span className={className}>{node}</span>;
        if (run.href) {
          node = (
            <a
              href={run.href}
              target="_blank"
              rel="noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
            >
              {node}
            </a>
          );
        }
        return <Fragment key={i}>{node}</Fragment>;
      })}
    </>
  );
}

function BlockView({ block }: { block: NotionBlock }) {
  switch (block.type) {
    case "paragraph":
      return block.rich.length === 0 ? (
        <div className="h-3" aria-hidden />
      ) : (
        <p className="text-sm leading-relaxed text-foreground/90">
          <RichText runs={block.rich} />
        </p>
      );
    case "heading_1":
      return (
        <h2 className="mt-6 font-display text-lg font-semibold tracking-tight">
          <RichText runs={block.rich} />
        </h2>
      );
    case "heading_2":
      return (
        <h3 className="mt-5 font-display text-base font-semibold tracking-tight">
          <RichText runs={block.rich} />
        </h3>
      );
    case "heading_3":
      return (
        <h4 className="mt-4 font-display text-sm font-semibold tracking-tight">
          <RichText runs={block.rich} />
        </h4>
      );
    case "to_do":
      return (
        <label className="flex items-start gap-2 text-sm text-foreground/90">
          <input
            type="checkbox"
            checked={block.checked}
            readOnly
            className="mt-1 h-3.5 w-3.5 rounded border-border accent-accent"
          />
          <span className={cn(block.checked && "text-muted-foreground line-through")}>
            <RichText runs={block.rich} />
          </span>
        </label>
      );
    case "quote":
      return (
        <blockquote className="border-l-2 border-accent/50 pl-3 text-sm italic text-foreground/80">
          <RichText runs={block.rich} />
        </blockquote>
      );
    case "callout":
      return (
        <div className="flex items-start gap-2 rounded-lg border border-border/70 bg-muted/30 px-3 py-2 text-sm text-foreground/90">
          {block.emoji && <span aria-hidden>{block.emoji}</span>}
          <span>
            <RichText runs={block.rich} />
          </span>
        </div>
      );
    case "code":
      return (
        <pre className="overflow-x-auto rounded-lg border border-border/70 bg-muted/40 p-3 text-xs">
          <code className="font-mono text-foreground/90">
            {block.rich.map((r) => r.text).join("")}
          </code>
        </pre>
      );
    case "divider":
      return <hr className="border-border/60" />;
    case "image":
      return (
        <figure className="space-y-1">
          <img
            src={block.url}
            alt={block.caption.map((r) => r.text).join("") || ""}
            className="max-w-full rounded-lg border border-border/60"
            loading="lazy"
          />
          {block.caption.length > 0 && (
            <figcaption className="text-xs text-muted-foreground">
              <RichText runs={block.caption} />
            </figcaption>
          )}
        </figure>
      );
    case "bookmark":
      return (
        <a
          href={block.url}
          target="_blank"
          rel="noreferrer"
          className="block truncate rounded-lg border border-border/70 bg-muted/20 px-3 py-2 text-sm text-accent hover:bg-muted/40"
        >
          {block.caption.length > 0 ? (
            <RichText runs={block.caption} />
          ) : (
            block.url
          )}
        </a>
      );
    case "table":
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className="border-b border-border/50">
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={cn(
                        "border-r border-border/40 px-3 py-1.5 last:border-r-0",
                        ri === 0 && "bg-muted/30 font-medium",
                      )}
                    >
                      <RichText runs={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "toggle":
      return (
        <details className="rounded-lg border border-border/60 px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium">
            <RichText runs={block.rich} />
          </summary>
          {block.children.length > 0 && (
            <div className="mt-2">
              <NotionBlocks blocks={block.children} />
            </div>
          )}
        </details>
      );
    case "bulleted_list_item":
    case "numbered_list_item":
      // Rendered via grouping in NotionBlocks; this branch is a defensive
      // fallback if an item ever reaches here standalone.
      return (
        <li className="ml-5 list-disc text-sm text-foreground/90">
          <RichText runs={block.rich} />
        </li>
      );
    case "unsupported":
      return (
        <p className="rounded border border-dashed border-border/60 px-2 py-1 text-xs text-muted-foreground">
          Unsupported block: {block.notionType}
        </p>
      );
    default:
      return null;
  }
}

// List item with optional nested children.
function ListItem({ block }: { block: Extract<NotionBlock, { type: "bulleted_list_item" | "numbered_list_item" }> }) {
  return (
    <li className="text-sm leading-relaxed text-foreground/90">
      <RichText runs={block.rich} />
      {block.children.length > 0 && (
        <div className="mt-1">
          <NotionBlocks blocks={block.children} />
        </div>
      )}
    </li>
  );
}

// Renders one normalized row property's value. Names come from the Notion DB
// schema (e.g. "Integration Status", "API Documentation").
function PropertyValue({ property }: { property: NotionProperty }) {
  switch (property.type) {
    case "text":
    case "number":
      return <span className="text-foreground/90">{String(property.value)}</span>;
    case "date":
      return <span className="text-foreground/90">{property.value}</span>;
    case "url":
      return (
        <a
          href={property.value}
          target="_blank"
          rel="noreferrer"
          className="break-all text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {property.value}
        </a>
      );
    case "email":
      return (
        <a
          href={`mailto:${property.value}`}
          className="break-all text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {property.value}
        </a>
      );
    case "phone":
      return (
        <a
          href={`tel:${property.value}`}
          className="text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
        >
          {property.value}
        </a>
      );
    case "checkbox":
      return property.value ? (
        <span className="inline-flex items-center gap-1 text-foreground/90">
          <Check className="h-3.5 w-3.5 text-accent" /> Yes
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <Minus className="h-3.5 w-3.5" /> No
        </span>
      );
    case "select":
      return (
        <span className="inline-flex rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
          {property.value}
        </span>
      );
    case "multi_select":
      return (
        <span className="flex flex-wrap gap-1">
          {property.value.map((v) => (
            <span
              key={v}
              className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
            >
              {v}
            </span>
          ))}
        </span>
      );
    default:
      return null;
  }
}

// Property grid for a KB row — the structured fields (status, contacts, API
// docs, markets…). Empty values are dropped server-side, so anything here is
// populated.
export function NotionProperties({
  properties,
}: {
  properties: NotionProperty[];
}) {
  if (properties.length === 0) return null;
  return (
    <dl className="grid gap-x-6 gap-y-2 rounded-xl border border-border/60 bg-card px-5 py-4 sm:grid-cols-2">
      {properties.map((property) => (
        <div
          key={property.name}
          className="flex flex-col gap-0.5 border-b border-border/40 pb-2 last:border-b-0 sm:[&:nth-last-child(-n+1)]:border-b-0"
        >
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {property.name}
          </dt>
          <dd className="text-sm">
            <PropertyValue property={property} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function NotionBlocks({ blocks }: { blocks: NotionBlock[] }) {
  // Group consecutive list items of the same kind into a single <ul>/<ol>.
  const out: React.ReactNode[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (
      block.type === "bulleted_list_item" ||
      block.type === "numbered_list_item"
    ) {
      const kind = block.type;
      const items: Extract<
        NotionBlock,
        { type: "bulleted_list_item" | "numbered_list_item" }
      >[] = [];
      while (i < blocks.length && blocks[i].type === kind) {
        items.push(blocks[i] as (typeof items)[number]);
        i += 1;
      }
      const ListTag = kind === "bulleted_list_item" ? "ul" : "ol";
      out.push(
        <ListTag
          key={`list-${i}`}
          className={cn(
            "ml-5 space-y-1",
            kind === "bulleted_list_item" ? "list-disc" : "list-decimal",
          )}
        >
          {items.map((it, idx) => (
            <ListItem key={idx} block={it} />
          ))}
        </ListTag>,
      );
      continue;
    }
    out.push(<BlockView key={i} block={block} />);
    i += 1;
  }
  return <div className="space-y-2">{out}</div>;
}
